import React, { useMemo } from "react";
import Field from "./shared/Field";

function normalizeTestimonialsTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();

  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";

  return "template1";
}

function Group({ title, subtitle, defaultOpen = true, children, toggleLabel }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{toggleLabel || "Toggle"}</div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              active ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function TestimonialsSectionEditor({ config, template, onChange, t = (k, f) => f }) {
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeTestimonialsTemplate(template), [template]);

  const isTemplate1 = tplKey === "template1";
  const isTemplate2 = tplKey === "template2";
  const isTemplate3 = tplKey === "template3";

  const testimonials = Array.isArray(cfg.testimonials) ? cfg.testimonials : [];

  const updateTestimonial = (index, key, value) => {
    const updated = [...testimonials];
    updated[index] = {
      ...(updated[index] || {}),
      [key]: value,
    };
    onChange("testimonials", updated);
  };

  const addTestimonial = () => {
    const baseItem = {
      name: "",
      role: "",
      content: "",
    };

    let templateItem = baseItem;

    if (isTemplate1) {
      templateItem = {
        ...baseItem,
        avatar: "",
        rating: 5,
      };
    }

    if (isTemplate2) {
      templateItem = {
        ...baseItem,
        avatar: "",
        company: "",
      };
    }

    if (isTemplate3) {
      templateItem = {
        ...baseItem,
        metrics: "",
      };
    }

    onChange("testimonials", [...testimonials, templateItem]);
  };

  const removeTestimonial = (index) => {
    const updated = [...testimonials];
    updated.splice(index, 1);
    onChange("testimonials", updated);
  };

  return (
    <div className="space-y-4">
      <Group
        title={t("testimonialsEditor.group.basics", "Section Basics")}
        subtitle={t("testimonialsEditor.group.basicsHelp", "Main section text and layout direction")}
        toggleLabel={t("testimonialsEditor.group.basicsToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("testimonialsEditor.header.layoutDirection", "Layout Direction")}
            </div>

            <Segmented
              value={cfg.direction || "ltr"}
              onChange={(v) => onChange("direction", v)}
              options={[
                { value: "ltr", label: t("testimonialsEditor.direction.ltr", "LTR") },
                { value: "rtl", label: t("testimonialsEditor.direction.rtl", "RTL") },
              ]}
            />

            <div className="text-xs text-gray-500 mt-2">
              {t(
                "testimonialsEditor.header.layoutDirectionHelp",
                "Use RTL for Arabic and Hebrew, LTR for English."
              )}
            </div>
          </div>

          <Field
            label={t("testimonialsEditor.header.title", "Title")}
            value={cfg.title || ""}
            onChange={(v) => onChange("title", v)}
          />

          {(isTemplate2 || isTemplate3) && (
            <Field
              label={t("testimonialsEditor.header.subtitle", "Subtitle")}
              type="textarea"
              value={cfg.subtitle || ""}
              onChange={(v) => onChange("subtitle", v)}
            />
          )}
        </div>
      </Group>

      <Group
        title={t("testimonialsEditor.group.testimonials", "Testimonials List")}
        subtitle={t("testimonialsEditor.group.testimonialsHelp", "Add, edit, and remove customer testimonials")}
        toggleLabel={t("testimonialsEditor.group.testimonialsToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {t("testimonialsEditor.list.title", "Testimonials")}
            </h3>

            <button
              type="button"
              onClick={addTestimonial}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
            >
              {t("testimonialsEditor.list.add", "Add Testimonial")}
            </button>
          </div>

          {testimonials.length === 0 ? (
            <div className="text-sm text-gray-500">
              {t("testimonialsEditor.list.empty", "No testimonials yet. Click Add Testimonial to create the first one.")}
            </div>
          ) : null}

          {testimonials.map((testimonial, index) => (
            <details
              key={index}
              open
              className="rounded-xl border border-gray-200 bg-gray-50"
            >
              <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-700">
                    {t("testimonialsEditor.list.item", "Testimonial")} {index + 1}
                    {testimonial?.name ? (
                      <span className="text-gray-400 font-normal"> • {testimonial.name}</span>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {t("testimonialsEditor.list.itemToggle", "Toggle")}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeTestimonial(index);
                    }}
                    className="text-red-500 text-sm hover:underline"
                  >
                    {t("testimonialsEditor.common.remove", "Remove")}
                  </button>
                </div>
              </summary>

              <div className="px-4 pb-4 pt-1 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field
                    label={t("testimonialsEditor.fields.name", "Name")}
                    value={testimonial?.name || ""}
                    onChange={(v) => updateTestimonial(index, "name", v)}
                  />

                  <Field
                    label={t("testimonialsEditor.fields.role", "Role")}
                    value={testimonial?.role || ""}
                    onChange={(v) => updateTestimonial(index, "role", v)}
                  />
                </div>

                {isTemplate1 && (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field
                        label={t("testimonialsEditor.fields.avatar", "Avatar (Initials or URL)")}
                        value={testimonial?.avatar || ""}
                        onChange={(v) => updateTestimonial(index, "avatar", v)}
                        placeholder={t("testimonialsEditor.fields.avatarPlaceholder", "AJ or image URL")}
                      />

                      <Field
                        label={t("testimonialsEditor.fields.rating", "Rating (0-5)")}
                        type="number"
                        value={testimonial?.rating ?? 5}
                        onChange={(v) => updateTestimonial(index, "rating", Number(v))}
                      />
                    </div>

                    <Field
                      label={t("testimonialsEditor.fields.content", "Content")}
                      type="textarea"
                      value={testimonial?.content || ""}
                      onChange={(v) => updateTestimonial(index, "content", v)}
                    />
                  </>
                )}

                {isTemplate2 && (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field
                        label={t("testimonialsEditor.fields.company", "Company")}
                        value={testimonial?.company || ""}
                        onChange={(v) => updateTestimonial(index, "company", v)}
                      />

                      <Field
                        label={t("testimonialsEditor.fields.avatar", "Avatar (Initials or URL)")}
                        value={testimonial?.avatar || ""}
                        onChange={(v) => updateTestimonial(index, "avatar", v)}
                        placeholder={t("testimonialsEditor.fields.avatarPlaceholder", "AJ or image URL")}
                      />
                    </div>

                    <Field
                      label={t("testimonialsEditor.fields.content", "Content")}
                      type="textarea"
                      value={testimonial?.content || ""}
                      onChange={(v) => updateTestimonial(index, "content", v)}
                    />
                  </>
                )}

                {isTemplate3 && (
                  <>
                    <Field
                      label={t("testimonialsEditor.fields.metrics", "Metrics")}
                      value={testimonial?.metrics || ""}
                      onChange={(v) => updateTestimonial(index, "metrics", v)}
                      placeholder={t("testimonialsEditor.fields.metricsPlaceholder", "65% growth")}
                    />

                    <Field
                      label={t("testimonialsEditor.fields.content", "Content")}
                      type="textarea"
                      value={testimonial?.content || ""}
                      onChange={(v) => updateTestimonial(index, "content", v)}
                    />
                  </>
                )}
              </div>
            </details>
          ))}
        </div>
      </Group>
    </div>
  );
}